import { useState } from 'react';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Switch,
  useToast
} from '@morressier/alembic';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import EmptyReviewQueue from './EmptyReviewQueue';
import ReviewQueueTable from './ReviewQueueTable';
import { IReviewer } from '@publishing-engine/domain/models';
import {
  useInviteReviewer,
  useRemoveEnqueuedReviewer,
  useReorderReviewerQueue,
  useRequestReviewInRevision
} from 'src/api/commands';
import { useSubmissionId } from 'src/hooks/useSubmissionId';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import ConfirmDeleteEnqueuedReviewerModal from './ConfirmDeleteEnqueuedReviewerModal';
import { Overlay } from 'src/components/Overlay';
import { ensureUser, IReviewerInvitationResponse } from 'src/api/queries';
import ConfirmIndividualInviteModal from './ConfirmIndividualInviteModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useActivateReviewerQueue } from '../../../../api/commands';
import { FormProvider, useForm } from 'react-hook-form';
import { EmailContentForm } from '../../../EmailModule';
import ProtectedWorkflowAction from '../../../ProtectedAction/ProtectedWorkflowAction';
import { normalizeRecipients } from '../../../../utils/normalizeEmailRecipients';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import RIWithSubmissionDetails from '../ReviewerSearch/RIWithSubmissionDetails';
import { useCommandEmailTemplates } from '../../../../hooks/useCommandEmailTemplates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faRectangleHistoryCircleUser
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { useSendReviewerInvitations } from '../../../../hooks/useSendReviewerInvitation';
import { IEmailCommand } from '@publishing-engine/domain/types/email-template';
import { scrollToFirstError } from '../../../../utils/scrollToFirstError';

interface Props {
  onInviteReviewer?: VoidFunction;
  reviewerInvitations?: IReviewerInvitationResponse;
  onViewReviewerDetails: (reviewer: IReviewer) => void;
  renderInviteCallToAction?: boolean;
}

const ReviewQueue = ({
  onInviteReviewer,
  reviewerInvitations,
  onViewReviewerDetails,
  renderInviteCallToAction = true
}: Props) => {
  const { availableActions, organizationId, getDeadlinesForReviewState } =
    useSubmissionData();
  const canManageQueue = availableActions.contains('EnqueueReviewRequest');
  const { submissionContext } = useSubmissionContextData();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showComposeInvitationModal, setShowComposeInvitationModal] =
    useState<boolean>(false);
  const [deleteReviewerEmails, setDeleteReviewerEmails] = useState<string[]>(
    []
  );
  const [inviteNowReviewerEmails, setInviteNowReviewerEmails] = useState<
    string[]
  >([]);

  const queue = reviewerInvitations?.queue || [];
  const autoInvite = reviewerInvitations?.autoInvite;

  const submissionContextId = useCurrentContextId() ?? '';
  const submissionId = useSubmissionId() ?? '';
  const toast = useToast();
  const invitationDeadline =
    getDeadlinesForReviewState('invited')?.regular ?? 0;

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const reviewQueueQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviewer-invitations'
  ];
  const reviewsQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviews'
  ];
  const reviewersKey = ['reviewer-list', { submissionContextId, submissionId }];
  const reviewListKey = ['review-list', { submissionContextId, submissionId }];

  const { mutateAsync: mutateRemoveEnqueuedReviewer } =
    useRemoveEnqueuedReviewer({
      submissionId,
      submissionContextId,
      queryKey: reviewQueueQueryKey,
      organizationId
    });

  const inviteReviewerMutation = useInviteReviewer(
    submissionId,
    submissionContextId,
    reviewListKey,
    organizationId
  );

  const requestReviewInRevisionMutation = useRequestReviewInRevision(
    submissionId,
    submissionContextId,
    reviewListKey,
    organizationId
  );

  const reorderReviewerQueueMutation = useReorderReviewerQueue({
    submissionContextId,
    submissionId,
    reviewerInvitationId: reviewerInvitations?.id!,
    queryKey: reviewQueueQueryKey
  });

  const activateReviewerQueueMutation = useActivateReviewerQueue(
    submissionId,
    submissionContextId,
    [reviewersKey, reviewQueueQueryKey, reviewsQueryKey, reviewListKey],
    organizationId
  );

  const handleRemoveEnqueuedReviewer = (
    reviewerInvitationId: string,
    reviewerEmail: string[]
  ) => {
    reviewerEmail.map(async email => {
      const response = await mutateRemoveEnqueuedReviewer({
        reviewerInvitationId,
        reviewerEmail: email
      });
      if (response.isSuccess) {
        toast({
          variant: 'success',
          title: `${email} Removed from Queue`
        });
      } else {
        toast({
          variant: 'error',
          title: `Error Removing ${email} from Queue`
        });
      }
    });
    setDeleteReviewerEmails([]);
  };

  const { sendReviewerInvitations, isProcessing } = useSendReviewerInvitations({
    ensureUser,
    availableActions,
    inviteReviewerMutation,
    requestReviewInRevisionMutation,
    toast
  });

  const handleInviteNowReviewers = async () => {
    if (!templates || templates.length === 0) {
      toast({
        variant: 'error',
        title: 'Missing Email Template',
        description:
          'Unable to send invitations because no email template exists.',
        isClosable: true
      });
      return;
    }

    const defaultIndex = templates.findIndex(t => t.default);
    const index = defaultIndex === -1 ? 0 : defaultIndex;

    const template = templates[index];

    if (!template) {
      toast({
        variant: 'error',
        title: 'Missing Email Template',
        description:
          'Unable to send invitations because no email template exists.',
        isClosable: true
      });
      return;
    }

    const baseEmailContext: IEmailCommand = {
      subject: template.subject,
      body: template.body,
      recipients: {
        to: template.recipients.to,
        cc: template.recipients.cc,
        bcc: template.recipients.bcc
      }
    };

    sendReviewerInvitations(inviteNowReviewerEmails, baseEmailContext, () => {
      setShowConfirmModal(false);
      setInviteNowReviewerEmails([]);
    });
  };

  const handleConfigAndInviteReviewers = async () => {
    const { getValues, trigger } = emailContentMethods;
    const baseEmailContext = getValues('emailContent');

    const templateRecipientsNormalized = normalizeRecipients(
      baseEmailContext.recipients
    );

    const isValid = await trigger();
    if (!isValid) {
      scrollToFirstError();
      return;
    }

    sendReviewerInvitations(
      inviteNowReviewerEmails,
      { ...baseEmailContext, recipients: { ...templateRecipientsNormalized } },
      () => {
        setShowComposeInvitationModal(false);
        setInviteNowReviewerEmails([]);
      }
    );
  };

  const handleReorderReviewerQueue = async (
    newPosition: number,
    currentPosition: number
  ): Promise<boolean> => {
    const response = await reorderReviewerQueueMutation.mutateAsync({
      newPosition,
      currentPosition
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Reviewer Queue Reordered'
      });
      return true;
    } else {
      toast({
        variant: 'error',
        title: 'Reviewer Queue Reorder Failed'
      });
      return false;
    }
  };

  const renderContent = () => {
    if (!queue.length && renderInviteCallToAction) {
      return (
        <EmptyReviewQueue onInviteReviewer={onInviteReviewer ?? (() => {})} />
      );
    }

    return (
      <>
        {renderInviteCallToAction && (
          <Flex gap={4} justifyContent="space-between">
            <ProtectedWorkflowAction actionName="EnqueueReviewRequest">
              <Button
                leftIcon={
                  <FontAwesomeIcon icon={faRectangleHistoryCircleUser} />
                }
                onClick={onInviteReviewer ?? (() => {})}
                isDisabled={!availableActions.contains('EnqueueReviewRequest')}
              >
                Add Reviewers to Queue
              </Button>
            </ProtectedWorkflowAction>
          </Flex>
        )}
        <ReviewQueueTable
          reviewerInvitations={reviewerInvitations}
          onViewReviewerDetails={onViewReviewerDetails}
          onRemoveEnqueuedReviewer={setDeleteReviewerEmails}
          onInviteNowReviewer={emails => {
            setInviteNowReviewerEmails(emails);
            setShowConfirmModal(true);
          }}
          onInviteAndConfigReviewer={emails => {
            setInviteNowReviewerEmails(emails);
            setShowComposeInvitationModal(true);
          }}
          reorderReviewerQueue={handleReorderReviewerQueue}
          isProcessing={isProcessing}
        />
      </>
    );
  };

  const commandName = availableActions.contains('RequestReviewInRevision')
    ? 'RequestReviewInRevision'
    : 'RequestReview';

  const { hasCustomizableTemplates, isLoading, templates } =
    useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName,
      query: { subtype: 'manual' }
    });

  if (showComposeInvitationModal && hasCustomizableTemplates) {
    return (
      <Overlay>
        <FormProvider {...emailContentMethods}>
          <RIWithSubmissionDetails
            closeModal={() => setShowComposeInvitationModal(false)}
            isLoadingTemplates={isLoading}
            emailTemplates={templates}
            emails={
              inviteNowReviewerEmails.length ? inviteNowReviewerEmails : []
            }
            commandName={commandName}
          />

          <Flex
            padding={4}
            bottom={0}
            zIndex={99}
            justifyContent="center"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Flex width="100%" justifyContent="space-between">
              <ButtonText
                variant="caution"
                onClick={() => {
                  setInviteNowReviewerEmails([]);
                  setShowComposeInvitationModal(false);
                }}
              >
                Exit
              </ButtonText>

              <Flex gap={4}>
                <Button
                  variant="primary"
                  onClick={emailContentMethods.handleSubmit(
                    handleConfigAndInviteReviewers
                  )}
                >
                  Invite Now
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </FormProvider>
      </Overlay>
    );
  }

  return (
    <Flex flexDirection="column" gap={6}>
      <Flex gap={2} flexDirection="column">
        <Text textStyle="headingSection">
          Reviewer Queue
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            label={
              canManageQueue
                ? submissionContext?.config.cms.editorialViewForm?.reviews
                    .inviteReviewersTooltip
                : submissionContext?.config.cms.editorialViewForm?.reviews
                    .addReviewersToQueue
            }
            borderRadius={4}
          >
            <ButtonIcon
              size="small"
              ml={1}
              ariaLabel="Help Icon"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        </Text>
      </Flex>

      {showConfirmModal && (
        <ConfirmIndividualInviteModal
          onInvite={() => handleInviteNowReviewers()}
          onClose={() => setShowConfirmModal(false)}
          invitationDeadline={invitationDeadline}
          isLoading={isProcessing}
        />
      )}

      {!!deleteReviewerEmails.length && (
        <ConfirmDeleteEnqueuedReviewerModal
          onDelete={() =>
            handleRemoveEnqueuedReviewer(
              reviewerInvitations?.id!,
              deleteReviewerEmails
            )
          }
          onClose={() => setDeleteReviewerEmails([])}
        />
      )}

      {queue.length > 0 && canManageQueue && (
        <Flex flexDirection="row" gap={2} alignItems="flex-start">
          <Switch
            isDisabled={activateReviewerQueueMutation.isLoading}
            isChecked={autoInvite}
            onChange={e => {
              activateReviewerQueueMutation.mutateAsync(!!e.target.checked);
            }}
          />
          <Flex flexDirection="column">
            <Text textStyle="headingLine">Activate Automatic Queue</Text>
            <Text textStyle="textBody">
              Enable this to automatically invite the next reviewer in the queue
              if someone declines or does not respond. Invitations proceed from
              the top of the list downwards.
            </Text>
          </Flex>
        </Flex>
      )}

      {renderContent()}
    </Flex>
  );
};

export default ReviewQueue;
